"use client";
import MenuItem from "./menu/MenuItem";
import TopWinnerSpecial from "./TopWinnerSpecial";
import {
  ICompanySubMenu,
  MenuItemType,
} from "@/api/AppApi/services/company/interfaces/ICompany";
import { Nav } from "@/utils/bootstrap";
import {
  isKimbo,
  isKWCompliance,
  IsTopWinner,
} from "@/utils/functions/global-constants";
import MenuLink from "./menu/MenuLink";
import { Fragment, useState } from "react";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import useIsClient from "@/hooks/useIsClient";
import { useLang } from "@/providers/langProvider";

interface Props {
  isMobile?: boolean;
  isNewHeader?: boolean;
}
const maxTopLevel = 5; // Maximum items for top-level menu
const maxSubMenu = 20; // Maximum items for sub-menus

function rec(
  array: ICompanySubMenu[],
  more: string,
  maxTopLevel: number,
  maxSubMenu: number,
) {
  // Handle top-level array length
  if (
    array.length > maxTopLevel + 3 &&
    array[maxTopLevel]?.type !== MenuItemType.more
  ) {
    const slicedArray = array.slice(0, maxTopLevel);
    const moreSubMenu = array.slice(maxTopLevel);
    slicedArray.push({
      value: "more",
      type: MenuItemType.more,
      title: more,
      sub_menu: rec(moreSubMenu, more, maxSubMenu, maxSubMenu), // Recursively process the more sub-menu with sub-menu rules
    });
    array = slicedArray;
  }

  // Process sub-menus recursively
  for (let i in array) {
    if (
      array[i] &&
      array[i].sub_menu &&
      Array.isArray(array[i].sub_menu) &&
      array[i].sub_menu.length > maxSubMenu + 3 &&
      array[i].sub_menu[maxSubMenu].type !== MenuItemType.more
    ) {
      const subMenu = array[i].sub_menu.slice();
      array[i] = { ...array[i], sub_menu: subMenu.slice(0, maxSubMenu) };
      array[i].sub_menu.push({
        value: "more",
        type: MenuItemType.more,
        title: more,
        sub_menu: rec(subMenu.slice(maxSubMenu), more, maxSubMenu, maxSubMenu), // Recursively process the more sub-menu
      });
    }
    if (array[i] && array[i].sub_menu) {
      array[i].sub_menu = rec(array[i].sub_menu, more, maxSubMenu, maxSubMenu);
    }
  }
  return array;
}
const useArraySlicer = (array: []) => {
  const [isSliced, setIsSliced] = useState(true);
  const arraySlicer = (
    length: number,
    showMore: string,
    showLess: string,
  ): any[] => {
    if (array.length > length) {
      if (isSliced) {
        const showMoreObj: ICompanySubMenu & { onClick: () => void } = {
          title: showMore,
          onClick: () => setIsSliced(false),
          value: "",
          sub_menu: [],
          type: MenuItemType.more,
        };
        const newArray = [...array.slice(0, length), , showMoreObj];
        return newArray;
      } else {
        const showLessObj: ICompanySubMenu & { onClick: () => void } = {
          title: showLess,
          onClick: () => setIsSliced(true),
          value: "",
          sub_menu: [],
          type: MenuItemType.more,
        };
        const newArray = [...array, showLessObj];
        return newArray;
      }
    }
    return array;
  };

  return arraySlicer;
};

const CompanyMenus: React.FC<Props> = ({ isMobile = false, isNewHeader }) => {
  const t = useTranslation();
  const isClient = useIsClient();
  const getCompany = useGetCompanyQuery();
  const { menu: header_menu, mobile_menu } = getCompany?.data?.result ?? {};
  const menu = isMobile
    ? [
        ...(header_menu ?? []).filter(
          (item) => item.type !== MenuItemType.inner_page,
        ),
        ...(mobile_menu || []),
      ]
    : header_menu;
  const kwc = isKWCompliance(getCompany.data?.result!);
  const Kimbo = isKimbo(getCompany.data?.result!);
  const lang = useLang();

  const categoryMenu = [...(menu ?? [])].find(
    (menu) => menu.type === MenuItemType.menu_item,
  ) ?? { sub_menu: [], title: "", type: MenuItemType.more, value: "" };

  const categoryMenuToDisplay = Kimbo
    ? [...(categoryMenu?.sub_menu ?? [])]
    : [categoryMenu];

  const otherMenuItems = [...(menu ?? [])].filter(
    (menu, idx) => !(menu.type === MenuItemType.menu_item && idx === 0),
  );

  const filteredMenu = [...categoryMenuToDisplay, ...otherMenuItems]?.filter(
    (item) => {
      if (kwc && item?.type === MenuItemType.menu_item) {
        return false;
      }
      return true;
    },
  );

  const KWComplianceCourseLink = menu?.find(
    (item) =>
      item.type === MenuItemType.menu_item &&
      item.sub_menu?.every((item) => item.type === MenuItemType.category),
  )?.sub_menu?.[1];

  const arraySlicer = useArraySlicer(
    ((filteredMenu || [])?.[0]?.sub_menu as []) || [],
  );
  if (!filteredMenu || filteredMenu?.length === 0) return null;
  let pcMenu: ICompanySubMenu[] = [];

  if (!isMobile)
    pcMenu = addParentId(
      rec(filteredMenu, t.more, isNewHeader ? 12 : maxTopLevel, maxSubMenu),
    );
  const mobileMenu = addParentId([
    {
      ...filteredMenu[0],
      sub_menu: arraySlicer(10, t.MoreCategories, t.LessCategories),
    },
    ...filteredMenu.slice(1),
  ]);

  if (!isClient) {
    return <></>;
  }
  if (kwc) {
    const links: ICompanySubMenu[] =
      lang === "ar"
        ? [
            {
              title: "من نحن",
              type: MenuItemType.inner_page,
              value: "about-us-347",
              sub_menu: [],
            },
            {
              title: "دورات",
              type: MenuItemType.category,
              value: "460",
              sub_menu: [],
            },

            {
              title: "خدماتنا",
              type: MenuItemType.inner_page,
              value: "services-348",
              sub_menu: [
                {
                  title: "مكافحة غسل الأموال وتمويل الإرهاب",
                  type: MenuItemType.inner_page,
                  value: "686",
                  sub_menu: [],
                },
                {
                  title: "التدريب",
                  type: MenuItemType.inner_page,
                  value: "688",
                  sub_menu: [],
                },
                {
                  title: "الخدمات الاستشارية ",
                  type: MenuItemType.inner_page,
                  value: "691",
                  sub_menu: [],
                },
                {
                  title: "قطاع المهن والخدمات المالية",
                  type: MenuItemType.inner_page,
                  value: "694",
                  sub_menu: [],
                },
                {
                  title: "قطاع المهن غير المالية",
                  type: MenuItemType.inner_page,
                  value: "696",
                  sub_menu: [],
                },
                {
                  title: "منظمات العمل المدني والجمعيات الخيرية",
                  type: MenuItemType.inner_page,
                  value: "701",
                  sub_menu: [],
                },
                {
                  title: "خدمات التوظيف",
                  type: MenuItemType.inner_page,
                  value: "702",
                  sub_menu: [],
                },
              ],
            },
            {
              title: "شركاؤنا",
              type: MenuItemType.inner_page,
              value: "pertners-349",
              sub_menu: [],
            },
            {
              title: "الأخبار",
              type: MenuItemType.blogs,
              value: "blogs",
              sub_menu: [],
            },
            {
              title: "اتصل بنا",
              type: MenuItemType.contact_us,
              value: "",
              sub_menu: [],
            },
          ]
        : [
            {
              title: "About Us",
              type: MenuItemType.inner_page,
              value: "about-us-347",
              sub_menu: [],
            },
            {
              title: "Courses",
              type: MenuItemType.category,
              value: "460",
              sub_menu: [],
            },

            {
              title: "Services",
              type: MenuItemType.inner_page,
              value: "services-348",
              sub_menu: [
                {
                  title: "AML/CFT",
                  type: MenuItemType.inner_page,
                  value: "686",
                  sub_menu: [],
                },
                {
                  title: "Training",
                  type: MenuItemType.inner_page,
                  value: "688",
                  sub_menu: [],
                },
                {
                  title: "Consulting",
                  type: MenuItemType.inner_page,
                  value: "691",
                  sub_menu: [],
                },
                {
                  title: "Financial Services Providers",
                  type: MenuItemType.inner_page,
                  value: "694",
                  sub_menu: [],
                },
                {
                  title: "Non-Financial Professions",
                  type: MenuItemType.inner_page,
                  value: "696",
                  sub_menu: [],
                },
                {
                  title: "Charities & NFPs / NGOs",
                  type: MenuItemType.inner_page,
                  value: "701",
                  sub_menu: [],
                },
                {
                  title: "Recruitment Services",
                  type: MenuItemType.inner_page,
                  value: "702",
                  sub_menu: [],
                },
              ],
            },
            {
              title: "Partners",
              type: MenuItemType.inner_page,
              value: "pertners-349",
              sub_menu: [],
            },
            {
              title: "News",
              type: MenuItemType.blogs,
              value: "blogs",
              sub_menu: [],
            },
            {
              title: "Contact Us",
              type: MenuItemType.contact_us,
              value: "",
              sub_menu: [],
            },
          ];
    return (
      <Nav className="nav w-100 ">
        {links?.map((everyMenu, key, array) => (
          <Fragment key={key}>
            <MenuItem
              key={key}
              item={everyMenu as ICompanySubMenu}
              isMobile={isMobile}
              down
              dropdown={key === 0 ? false : true}
              toggle={everyMenu.sub_menu.length > 0 && key > 0}
              title={
                isMobile && (everyMenu.sub_menu.length < 1 || key === 0)
                  ? false
                  : everyMenu.title
              }
            />
            {isMobile && (key === 0 || key === array.length - 1) && (
              <hr className="m-0 h-0" />
            )}
          </Fragment>
        ))}
      </Nav>
    );
  }

  return (
    <Nav
      className="nav w-100 "
      style={isNewHeader ? { justifyContent: "center" } : undefined}
    >
      {IsTopWinner(getCompany.data?.result!) && (
        <MenuLink isMobile={isMobile} href={"/"} title={{ text: t.home }} />
      )}
      {KWComplianceCourseLink && kwc && (
        <MenuItem
          item={KWComplianceCourseLink as ICompanySubMenu}
          isMobile={isMobile}
          down
          toggle={KWComplianceCourseLink.sub_menu.length > 0}
          title={
            isMobile && KWComplianceCourseLink.sub_menu.length < 1
              ? false
              : KWComplianceCourseLink.title
          }
        />
      )}

      {(isMobile ? mobileMenu : pcMenu)?.map((everyMenu, key, array) => (
        <Fragment key={key}>
          <MenuItem
            key={key}
            item={everyMenu as ICompanySubMenu}
            isMobile={isMobile}
            down
            dropdown={key === 0 ? false : true}
            toggle={everyMenu.sub_menu.length > 0 && key > 0}
            title={
              isMobile && (everyMenu.sub_menu.length < 1 || key === 0)
                ? false
                : everyMenu.title
            }
          />
          {isMobile && (key === 0 || key === array.length - 1) && (
            <hr className="m-0 h-0" />
          )}
        </Fragment>
      ))}
      {IsTopWinner(getCompany.data?.result!) && (
        <TopWinnerSpecial isMobile={isMobile} />
      )}
    </Nav>
  );
};
export default CompanyMenus;

function addParentId(
  arr: ICompanySubMenu[],
  parentId: string | null = null,
): ICompanySubMenu[] {
  return arr.map((item) => ({
    ...item,
    parentId,
    ...(item.sub_menu
      ? { sub_menu: addParentId(item.sub_menu, item.value) }
      : {}),
  }));
}
